import React, { useState, useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion"; // For animations
import { API_BASE_URL } from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../Layout/Layout"; // Import the enhanced Layout component

const Footer = () => {
  return (
    <footer className="bg-gradient-to-r from-green-600 to-teal-500 text-white py-4 text-center shadow-inner">
      <p className="text-sm tracking-wide">
        Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.
      </p>
    </footer>
  );
};

const RaiseQuery = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = window.__appSession.getToken();
      if (!token) {
        toast.error("No authentication token found. Please log in.");
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setName(response.data.result.name || "");
        setPhone(response.data.result.phone || "");
        setValue("name", response.data.result.name || "");
        setValue("phone", response.data.result.phone || "");
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };
    fetchUserData();
  }, [setValue]);

  const onSubmit = async (data) => {
    const token = window.__appSession.getToken();
    if (!token) {
      toast.error("No authentication token found. Please log in.");
      return;
    }

    const formData = new FormData();
    images.forEach((image) => formData.append("images", image));
    formData.append("name", data.name);
    formData.append("phone", data.phone);
    formData.append("problem", data.problem);

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/raise-query`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Your query has been raised successfully!");
      setImages([]);
      reset({ name, phone, problem: "" }); // Reset form but keep fetched name and phone
    } catch (error) {
      console.error("Error submitting query:", error);
      toast.error("There was an error submitting your query.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    setImages([...e.target.files]);
  };

  return (
    <Layout>
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="flex-grow w-full min-h-[calc(100vh-4rem)] py-6 px-4 sm:px-6"
      >
        <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-lg mx-auto border border-gray-200 transition-all">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-6 text-[#25D366] tracking-tight font-serif">
            Raise a Query
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-gray-700 font-medium mb-2"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                {...register("name")}
                readOnly
                className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all opacity-75 cursor-not-allowed"
              />
            </div>

            {/* Phone Field */}
            <div>
              <label
                htmlFor="phone"
                className="block text-gray-700 font-medium mb-2"
              >
                Phone
              </label>
              <input
                type="text"
                id="phone"
                value={phone}
                {...register("phone", { required: "Phone number is required" })}
                className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
              />
              {errors.phone && (
                <span className="text-red-500 text-sm">
                  {errors.phone.message}
                </span>
              )}
            </div>

            {/* Problem Field */}
            <div>
              <label
                htmlFor="problem"
                className="block text-gray-700 font-medium mb-2"
              >
                Problem Description
              </label>
              <textarea
                id="problem"
                {...register("problem", {
                  required: "Problem description is required",
                })}
                className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
                rows="4"
              />
              {errors.problem && (
                <span className="text-red-500 text-sm">
                  {errors.problem.message}
                </span>
              )}
            </div>

            {/* Image Upload Field */}
            <div>
              <label
                htmlFor="images"
                className="block text-gray-700 font-medium mb-2"
              >
                Attach Screenshots
              </label>
              <input
                type="file"
                id="images"
                multiple
                onChange={handleImageChange}
                className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#25D366] file:text-white hover:file:bg-[#128C7E]"
              />
              {images.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  {images.length} file(s) selected
                </div>
              )}
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(37, 211, 102, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white py-3 rounded-lg font-semibold shadow-md hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
                    />
                  </svg>
                  Submitting...
                </div>
              ) : (
                "Submit"
              )}
            </motion.button>
          </form>
        </div>
      </motion.main>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
      />
    </Layout>
  );
};

export default RaiseQuery;
